// Service page content for Visa Cancellations (responding and review).
// Sourced from docs/research/australian-visa-pathways.md §9.
// COMPLIANCE: our role is narrow and clerical, read the notice with you, flag
// the deadline, get you to a registered migration agent fast, and gather their
// documents. A registered migration agent works out the grounds, responds,
// lodges, and represents you. No guarantees; stress urgency; the
// ART/Department decides.

const visaCancellations = {
  groupSlug: "review-appeals",
  slug: "visa-cancellations",
  title: "Visa Cancellations",
  eyebrow: "Review & Appeals",
  intro:
    "A visa cancellation is serious and time-critical. It can affect your right to stay, work, or study, and the windows to respond or seek review are very short. If your visa has been cancelled, or you have received a notice of intention to consider cancellation, the time to act is now. We read the notice with you, flag the deadline, and get you to a registered migration agent fast, then gather the documents they need. A registered migration agent works out the grounds, responds, lodges any review, and represents you.",

  chips: [
    { label: "What it is", value: "Responding to a cancelled visa" },
    { label: "Decided by", value: "The Department, reviewable at the ART" },
    { label: "Time limit", value: "Very short" },
    { label: "Priority", value: "Act immediately" },
  ],

  whoFor: [
    "People whose visa has been cancelled.",
    "People who have received a notice of intention to consider cancellation.",
    "Anyone facing cancellation on character, condition-breach, or information grounds.",
  ],

  essentials: [
    {
      title: "Respond to any notice fast",
      detail:
        "If you receive a notice of intention to consider cancellation, you usually have a short period to respond before a decision is made. A strong, timely response can prevent cancellation.",
    },
    {
      title: "Understand the ground",
      detail:
        "Cancellations happen for different reasons, such as a breach of visa conditions, character issues, or incorrect information. The ground shapes the response.",
    },
    {
      title: "Review may be available",
      detail:
        "Many cancellation decisions can be reviewed by the ART, but not all. A registered migration agent confirms whether yours can be.",
    },
    {
      title: "Very strict time limits",
      detail:
        "The windows to respond or to seek review can be very short, sometimes only days. Acting immediately matters.",
    },
    {
      title: "Your status in the meantime",
      detail:
        "Cancellation can affect your lawful status, work and study rights, and even lead to detention. A registered migration agent explains your position, and we make sure you reach one straight away.",
    },
    {
      title: "Honest assessment",
      detail:
        "A registered migration agent tells you candidly how strong your position is rather than giving false reassurance.",
    },
  ],

  process: [
    {
      title: "Act on the notice now",
      detail:
        "We read your cancellation notice or decision with you immediately, flag every deadline, and get you to a registered migration agent fast.",
    },
    {
      title: "Identify the ground and options",
      detail:
        "A registered migration agent works out why cancellation is in play and what responses or review rights you have.",
    },
    {
      title: "Prepare a strong response",
      detail:
        "Where there is a notice, we gather and organise the evidence, and a registered migration agent prepares the submissions to try to avoid cancellation.",
    },
    {
      title: "Lodge any review in time",
      detail:
        "If the visa is cancelled and the decision is reviewable, a registered migration agent lodges with the ART within the strict limit. We chase the documents so nothing stalls.",
    },
    {
      title: "Manage the process",
      detail:
        "A registered migration agent represents you through the response or review and advises on any further options. We keep the documents moving and keep you informed.",
    },
  ],

  faqs: [
    {
      q: "My visa was cancelled. What do I do first?",
      a: "Contact us immediately. The time limits to respond or seek review can be extremely short, and your lawful status may be affected right now. The sooner we see your notice, the sooner we get you to a registered migration agent.",
    },
    {
      q: "Can a cancellation be challenged?",
      a: "Often yes. If you receive a notice of intention to consider cancellation, a strong response may prevent it. If a visa is cancelled, many decisions can be reviewed by the ART. A registered migration agent confirms your specific options.",
    },
    {
      q: "Can you guarantee my visa will be reinstated?",
      a: "No. The Department and the ART make these decisions, and no one can guarantee the outcome. Our part is to read the notice with you, flag the deadline, get you to a registered migration agent fast, and gather the documents. A registered migration agent gives you an honest assessment and puts the case forward, quickly.",
    },
    {
      q: "What happens to my status while this is sorted out?",
      a: "It depends on the situation. Cancellation can affect your right to stay, work, or study, and in some cases lead to detention. A registered migration agent explains and manages your position, and we make sure you reach one from the moment you contact us.",
    },
  ],

  related: ["visa-refusals", "student-500", "partner-visa"],

  lastVerified: "20 June 2026",
};

export default visaCancellations;
